import $, {Cash} from 'cash-dom';
import EmbedVideoLoader from './embed-video-loader';
import {historyReplace, publish, subscribe} from '@js/humans/helper';
import qs from 'qs';

/**
 * Repository Module
 * Handles interactions and visibility based on viewport position.
 * @package KAA
 * @author Tim Jungmann <Tim@Humans-Machines.com>
 */
export default class Repository {
    /**
     * Singleton instance of the component
     */
    static #instance: Repository;

    /**
     * Cash body reference
     */
    protected $body: Cash;

    /**
     * Intersection Observer reference
     */
    protected observer: IntersectionObserver | null = null;

    /**
     * Stores timeout IDs for hover removal
     */
    protected hoverTimeouts: Map<HTMLElement, number> = new Map();

    /**
     * UI references
     */
    protected ui = {
        repoArticle: '.js-repo-article',
        repoToggle: '.js-repo-toggle',
    };

    /**
     * Intersection observer options
     */
    protected observerOptions = {
        rootMargin: '-10% 0% -40% 0%', // Middle 40% of viewport
        threshold: [0.05, 0.1, 0.15], // Fires when 50% of an element is visible
    };

    /**
     * Repository constructor
     */
    protected constructor() {
        this.$body = $('body');

        // Register click events globally
        this.registerClickEvents();

        // Register events based on device type
        if (this.isTouchDevice()) {
            this.initIntersectionObserver();
            this.registerLazyLoadEvents();
        } else {
            this.registerHoverEvents();
        }
    }

    /**
     * Returns the singleton instance of the component
     */
    public static getInstance(): Repository {
        if (!Repository.#instance) {
            Repository.#instance = new Repository();
        }

        return Repository.#instance;
    }

    /**
     * Checks if the device is touch-enabled
     */
    protected isTouchDevice(): boolean {
        return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    }

    /**
     * Register hover events for non-touch devices
     */
    protected registerHoverEvents(): void {
        this.$body.on('mouseenter', this.ui.repoArticle, this.onMouseEnterRepo.bind(this));
        this.$body.on('mouseleave', this.ui.repoArticle, this.onMouseLeaveRepo.bind(this));
    }

    /**
     * Registers click events for toggling repo articles
     */
    protected registerClickEvents(): void {
        this.$body.on(
            'click',
            `${this.ui.repoToggle}`,
            this.toggleRepoArticle.bind(this)
        );
    }

    /**
     * Toggles the expanded state of a repository article
     */
    protected async toggleRepoArticle(event: PointerEvent): Promise<void> {
        event.preventDefault();

        const $button = $(event.currentTarget as HTMLElement);
        const $article = $button.closest(this.ui.repoArticle);

        if (!$article.length) {
            return;
        }

        const isExpanded = $button.attr('aria-expanded') === 'true';

        if (isExpanded) {
            // Close article
            $article.removeClass('is-expanded');
            $button.attr('aria-expanded', 'false');

            // Reset video
            const $videoPlayer = $article.find('.js-video-player');
            if ($videoPlayer.length) {
                EmbedVideoLoader.getInstance().resetVideo($videoPlayer);
            }

            historyReplace({...history.state ?? {}, url: this.getRepositoryDetailUrl($article.data('slug'), 'remove')});
        } else {
            // Open article
            $article.addClass('is-expanded');
            $button.attr('aria-expanded', 'true');

            // Publish event for lazy initialization of slideshows
            publish('repository.expanded', {
                $article,
            });

            historyReplace({...history.state ?? {}, url: this.getRepositoryDetailUrl($article.data('slug'))});
        }
    }

    protected getRepositoryDetailUrl(slug: string, action: 'add' | 'remove' = 'add'): string {
        let params = {...qs.parse(window.location.search.replace(/^\?+/, ''))} as Record<string, string | null>;

        // @ts-ignore
        let expArticles: string[] = params?.expArticles?.trim()?.length > 0 ? params?.expArticles?.split(',') : [];
        if (action === 'add') {
            expArticles.push(slug);
        } else {
            expArticles = expArticles.filter(article => article !== slug);
        }

        params.expArticles = expArticles.length > 0 ? expArticles.join(',') : null;

        const filteredParams = Object.fromEntries(Object.entries(params).filter(([_, val]) => val !== null));

        return `${window.location.pathname}?${qs.stringify(filteredParams)}`;
    }

    /**
     * Initializes the Intersection Observer for touch devices
     */
    protected initIntersectionObserver(): void {
        if (this.observer !== null) return;

        this.observer = new IntersectionObserver(
            this.handleIntersection.bind(this),
            this.observerOptions
        );

        this.observeElements(document);
    }

    /**
     * Registers event listeners for lazy-loaded content (touch devices only)
     */
    protected registerLazyLoadEvents(): void {
        subscribe('listing.updated', (_topic, payload: { $container: Cash }) => {
            const container = payload.$container?.get?.(0);
            if (container) {
                this.observeElements(container);
            }
        });
    }

    /**
     * Observes repository article elements within the given context
     */
    protected observeElements(context: HTMLElement | Document): void {
        if (!this.observer) return;

        $(this.ui.repoArticle, context).each((_, element) => {
            this.observer?.observe(element);
        });
    }

    /**
     * Handles intersection changes for repository articles (Touch devices only)
     */
    protected handleIntersection(entries: IntersectionObserverEntry[]): void {
        entries.forEach((entry) => {
            const article = entry.target as HTMLElement;
            article.classList.toggle('is-active', entry.isIntersecting && entry.intersectionRatio > 0.1);
        });
    }

    /**
     * Handles mouse entering a repo article (non-touch devices)
     */
    protected onMouseEnterRepo(event: MouseEvent): void {
        const $article = $(event.currentTarget as HTMLElement);
        const articleElement = $article.get(0);

        if (!articleElement) return;

        // Clear any existing timeout
        if (this.hoverTimeouts.has(articleElement)) {
            clearTimeout(this.hoverTimeouts.get(articleElement) as number);
            this.hoverTimeouts.delete(articleElement);
        }

        // Add the active class
        $article.addClass('is-active');
    }

    /**
     * Handles mouse leaving a repo article (non-touch devices)
     */
    protected onMouseLeaveRepo(event: MouseEvent): void {
        const $article = $(event.currentTarget as HTMLElement);
        const articleElement = $article.get(0);

        if (!articleElement) return;

        // Set a timeout to remove the active class after 2 seconds
        const timeoutId = window.setTimeout(() => {
            $article.removeClass('is-active');
            this.hoverTimeouts.delete(articleElement);
        }, 1200);

        // Store the timeout ID
        this.hoverTimeouts.set(articleElement, timeoutId);
    }
}
